import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  canEditSamplingFishData,
} from "../../components/content/axiosConfig";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import Preloader from "../../components/custom/preLoader";
import showErrorToast from "../../components/toast/toast";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import SamplingDetailGallery from "../../components/sampling/SamplingDetailGallery";

/**
 * Session-level photo gallery for a sampling detail (not per fish sample).
 */
const SamplingGalleryViaPondDetail = () => {
  const { id } = useParams();
  const userDetails = getUserDetails();
  const canEdit = canEditSamplingFishData(userDetails);
  const [pond, setPond] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const details = getUserDetails();
      const detailParams = { id };
      const scoped = resolveScopedFisherID(details);
      if (scoped != null && scoped !== "") {
        detailParams.FisherID = scoped;
      }
      const res = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
        params: detailParams,
      });
      setPond(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        showErrorToast("Session expired. Please log in again.");
        window.location.reload();
      } else {
        showErrorToast(error.response?.data?.message || "Failed to load sampling session");
      }
      setPond(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    checkTokenValidity();
    CheckUserExpiry();
    loadDetail();
  }, [loadDetail]);

  if (isLoading) return <Preloader />;

  return (
    <div className="donezo-page donezo-workflow-page py-3">
      <div className="container-fluid px-3 px-md-4">
        <div className="mb-4">
          <PondDetailWorkflowPageHeader
            pondDetailId={id}
            section="gallery"
            detailName={pond?.Name}
            noBottomMargin
          />
        </div>

        <PondDetailWorkflowTabs id={id} active="gallery" />

        <div className="card border-0 shadow-sm" style={{ borderRadius: "14px" }}>
          <div className="card-body p-3 p-md-4">
            <h1 className="h5 fw-semibold text-dark mb-1">Sampling gallery</h1>
            <p className="small text-muted mb-4">
              Photos for this sampling session. These are not linked to individual fish samples.
            </p>
            <SamplingDetailGallery pondDetailId={id} canEdit={canEdit} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplingGalleryViaPondDetail;
